import type { ProjectNeedsWithLegalSkills } from '@axiom/validation';
import { useState } from 'react';

import { DirectEngagementsFormUtil } from './direct-engagements-form-util';
import { DirectEngagementFormDeadEndModalTypes } from './direct-engagements-form-dead-end-modal.types';

interface OnDeadEndSubmitParams {
  field: keyof ProjectNeedsWithLegalSkills;
  value: ProjectNeedsWithLegalSkills[keyof ProjectNeedsWithLegalSkills];
  formValues: Partial<ProjectNeedsWithLegalSkills> | undefined;
  currentModalType: DirectEngagementFormDeadEndModalTypes;
  lastInteractedField?: keyof ProjectNeedsWithLegalSkills | null;
}

type ProjectNeedsWithLegalSkillsSubset = Partial<
  Pick<ProjectNeedsWithLegalSkills, keyof ProjectNeedsWithLegalSkills>
>;

type EvaluatorType = (args: ProjectNeedsWithLegalSkillsSubset) => boolean;

interface ValidationHandler {
  type: DirectEngagementFormDeadEndModalTypes;
  evaluator: EvaluatorType;
  fields: Array<keyof ProjectNeedsWithLegalSkills>;
}

export const useDirectEngagementsFormDeadEnds = () => {
  const [deadEndValues, setDeadEndValues] =
    useState<Partial<ProjectNeedsWithLegalSkills> | null>(null);
  const [showDeadEndModal, setShowDeadEndModal] = useState(false);
  const [closedModals, setClosedModals] = useState(
    () => new Set<DirectEngagementFormDeadEndModalTypes>()
  );
  const [modalType, setModalType] =
    useState<DirectEngagementFormDeadEndModalTypes>(
      DirectEngagementFormDeadEndModalTypes.LEGAL_SUPPORT_HOURS
    );

  const evaluateFields = (
    evaluator: (args: ProjectNeedsWithLegalSkillsSubset) => boolean,
    fields: Array<keyof ProjectNeedsWithLegalSkills>,
    values: Partial<ProjectNeedsWithLegalSkills>
  ): boolean => {
    const relevantValues = Object.fromEntries(
      fields.map(field => [
        field,
        values[field] === null ? undefined : values[field],
      ])
    ) as Partial<ProjectNeedsWithLegalSkills>;

    return evaluator(relevantValues);
  };

  const validationHandlers = [
    {
      type: DirectEngagementFormDeadEndModalTypes.LEGAL_SUPPORT_HOURS,
      evaluator:
        DirectEngagementsFormUtil.shouldDisplayLegalSupportHoursWarning,
      fields: ['billingUnitsPerWeek', 'occupationType'] as Array<
        keyof ProjectNeedsWithLegalSkills
      >,
    },
    {
      type: DirectEngagementFormDeadEndModalTypes.MIN_PAY_STATES,
      evaluator: DirectEngagementsFormUtil.shouldDisplayMinPayStatesWarning,
      fields: [
        'billingUnitsPerWeek',
        'companyLocation',
        'occupationType',
      ] as Array<keyof ProjectNeedsWithLegalSkills>,
    },
    {
      type: DirectEngagementFormDeadEndModalTypes.PAY_AS_YOU_GO_BUDGET,
      evaluator: DirectEngagementsFormUtil.shouldDisplayPayAsYouGoBudgetWarning,
      fields: ['reservedType', 'maxBudgetPerHour'] as Array<
        keyof ProjectNeedsWithLegalSkills
      >,
    },
    {
      type: DirectEngagementFormDeadEndModalTypes.RESERVED_PRICING,
      evaluator: DirectEngagementsFormUtil.shouldDisplayReservePricingWarning,
      fields: ['billingUnitsPerWeek', 'reservedType'] as Array<
        keyof ProjectNeedsWithLegalSkills
      >,
    },
  ];

  const evaluateAndHandleModal = (
    values: Partial<ProjectNeedsWithLegalSkills>,
    handler: ValidationHandler
  ) => {
    const { evaluator, fields, type } = handler;
    const shouldShowModal = evaluateFields(evaluator, fields, values);
    if (shouldShowModal) {
      setModalType(type);
      setShowDeadEndModal(true);
      return true;
    }
    return false;
  };

  const handleFormUpdate = (
    field: keyof ProjectNeedsWithLegalSkills,
    values: Partial<ProjectNeedsWithLegalSkills>
  ) => {
    setClosedModals(new Set());

    setShowDeadEndModal(false);
    const applicableHandlers = validationHandlers.filter(({ fields }) =>
      fields.includes(field)
    );

    for (const handler of applicableHandlers) {
      const modalTriggered = evaluateAndHandleModal(values, handler);
      if (modalTriggered) break;
    }
  };

  const handleCloseModal = (
    currentModalType?: DirectEngagementFormDeadEndModalTypes,
    field?: keyof ProjectNeedsWithLegalSkills | null,
    formValues?: Partial<ProjectNeedsWithLegalSkills>
  ) => {
    const updatedClosedModals = new Set(closedModals);

    if (currentModalType) {
      updatedClosedModals.add(currentModalType);
      setClosedModals(updatedClosedModals);
    }
    setShowDeadEndModal(false);

    if (!currentModalType || !field) return;

    const filteredHandlers = validationHandlers.filter(
      ({ fields, type }) =>
        fields.includes(field) && !updatedClosedModals.has(type)
    );

    for (const handler of filteredHandlers) {
      const modalTriggered = evaluateAndHandleModal({ ...formValues }, handler);
      if (modalTriggered) {
        break;
      }
    }
  };

  const onDeadEndSubmit = ({
    field,
    value,
    formValues,
    currentModalType,
    lastInteractedField,
  }: OnDeadEndSubmitParams) => {
    const newValues = {
      [field]: value,
    } as Partial<ProjectNeedsWithLegalSkills>;
    setDeadEndValues(newValues);
    handleCloseModal();

    // Skip handler for currently open modal
    const filteredHandlers = validationHandlers.filter(
      ({ type }) => type !== currentModalType
    );

    for (const handler of filteredHandlers) {
      // Handler has the 'field' which is changed
      const handlerHasFields = handler.fields.includes(field);
      // Handler shares any field with the last interacted field
      const handlerSharesField = handler.fields.find(
        handlerfield => handlerfield === lastInteractedField
      );
      const isNotClosedModal = !closedModals.has(handler.type);

      if (handlerHasFields || (handlerSharesField && isNotClosedModal)) {
        const modalTriggered = evaluateAndHandleModal(
          { ...formValues, ...newValues },
          handler
        );
        if (modalTriggered) break;
      }
    }
  };

  return {
    deadEndValues,
    modalType,
    showDeadEndModal,
    onDeadEndSubmit,
    handleFormUpdate,
    handleCloseModal,
    setShowDeadEndModal,
  };
};
